import _ from 'lodash';

import {AnswerFolderModel, AnswerOrFolderModel, RAnswerFolder} from '../../models/api/answerFolderModel';
import {AnswerModel, RAnswer} from '../../models/api/answerModel';
import {ResourceMap} from '../../store/appTypes';

export function findAnswerOrFolderNestingLevel(
  answerOrFolder: AnswerOrFolderModel,
  folders: ResourceMap<AnswerFolderModel>,
  level = 0,
): number {
  const folderId = RAnswerFolder.is(answerOrFolder) ? answerOrFolder.parentFolderId : answerOrFolder.folderId;
  if (!folderId) {
    return level;
  }

  const parentFolder = folders[folderId];

  if (!parentFolder) {
    return level;
  }

  return findAnswerOrFolderNestingLevel(parentFolder, folders, level + 1);
}

export function findAnswerOrFolderParents(
  answerOrFolder: AnswerOrFolderModel,
  folders: ResourceMap<AnswerFolderModel>,
): ReadonlyArray<AnswerFolderModel> {
  if (RAnswerFolder.is(answerOrFolder)) {
    return _(answerOrFolder.folderPath)
      .map((id) => folders[id])
      .compact()
      .value();
  }

  const {folderId} = answerOrFolder;

  if (!folderId) {
    return [];
  }

  const parentFolder = folders[folderId];
  if (!parentFolder) {
    return [];
  }
  return [...findAnswerOrFolderParents(parentFolder, folders), parentFolder];
}

export function findAnswerOrFolderChildren(
  answerOrFolder: AnswerOrFolderModel,
  folders: ResourceMap<AnswerFolderModel>,
  answers: ResourceMap<AnswerModel>,
): ReadonlyArray<AnswerOrFolderModel> {
  // An answer can't have children
  if (RAnswer.is(answerOrFolder)) {
    return [];
  }

  const foldersAndAnswers = [...Object.values(folders), ...Object.values(answers)];
  const directChildren = _(foldersAndAnswers)
    .compact()
    .filter((item) => {
      if (RAnswer.is(item)) {
        return item.folderId === answerOrFolder.id;
      }

      return item.parentFolderId === answerOrFolder.id;
    })
    .value();

  const childrenOfChildren = _(directChildren)
    .flatMap((item) => findAnswerOrFolderChildren(item, folders, answers))
    .compact()
    .value();

  return _(directChildren).concat(childrenOfChildren).uniqBy(makeKeyForAnswerOrFolder).value();
}

export function makeKeyForAnswerOrFolder(item: AnswerOrFolderModel) {
  if (RAnswer.is(item)) {
    return `answer:${item.id}`;
  }

  return `folder:${item.id}`;
}
